import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue';
import { useDrag, useResize } from '@/ai/utils/utils';
import { Message, Position, Size, ScrollPosition } from '@/ai/utils/types';
import { buildWelcomePromptMessage } from '@/ai/utils/defined';
import { useInternational } from '@/common/locale';
import { storeToRefs } from 'pinia';
import { useChatStore } from '../store/chat';
import { useChatConversationsStore } from '../store/chatConversations';
import { useMyInfoStore } from '@/config/stores/my-info';

export const setup = () => {
  const { t } = useInternational();
  const chatStore = useChatStore();
  const { isChatWindowOpen, isLoading, chatPosition, chatMessages, partialBotMessage } =
    storeToRefs(chatStore);
  const {
    toggleChat,
    getChatMessage,
    stopLoading,
    sendStreamingMessage,
    cancelStreaming,
    resetConversation,
  } = chatStore;

  const container = ref<HTMLDivElement>();
  const inputRef = ref<HTMLInputElement | null>(null);
  const isShowDetail = ref<boolean>(false);
  const shouldRenderSlideDetail = ref<boolean>(false);

  const userInput = ref<string>('');
  const { myInfo: userInfo } = storeToRefs(useMyInfoStore());

  const position = computed<Position>({
    get: () => ({ ...chatPosition.value.window }),
    set: (newPosition) => {
      chatStore.setChatPosition({ window: newPosition });
    },
  });

  const size = computed<Size>({
    get: () => ({ ...chatPosition.value.windowSize }),
    set: (newSize) => {
      chatStore.setChatPosition({ windowSize: newSize });
    },
  });

  const scrollPosition = computed<ScrollPosition>({
    get: () => ({ ...chatPosition.value.windowScroll }),
    set: (newScroll) => {
      chatStore.setChatPosition({ windowScroll: newScroll });
    },
  });

  const { startResize } = useResize({ size, position });
  const { startDrag, stopDrag } = useDrag({ size, position });

  const addChatMessage = async (message: string) => {
    userInput.value = '';
    await sendStreamingMessage(message);
  };

  const sendMessage = async () => {
    const userMessage = userInput.value.trim();
    if (!userMessage) return;
    // 대화 전환(생성/선택) 중 발화 차단 — Enter 경로는 버튼 disabled 를 우회하므로
    // 핸들러에서도 가드해야 이전 대화로 저장되는 것을 막는다 (3차 리뷰 P1)
    if (useChatConversationsStore().switching) return;

    await addChatMessage(userMessage);
  };

  const handleMessageClick = async (message: string) => {
    await addChatMessage(message);
  };

  /**
   * 화면 v-for 가 사용하는 표시용 메시지 목록.
   * chatMessages 가 비어있으면 환영 메시지 (welcomeText) 1개를 임시로 표시 — 사용자가 처음
   * 채팅창을 열거나 '대화 초기화' 직후 빈 화면 대신 샘플 발화 버튼을 보여주기 위함.
   * 실제 chatMessages 배열에는 추가하지 않으므로 사용자 발화가 한 번이라도 들어가면 자연스럽게
   * 사라지고, 서버 history 도 오염되지 않는다.
   */
  /**
   * 긴 히스토리 렌더 저하 방지 — 한 번에 최근 HISTORY_WINDOW 개만 렌더하고,
   * '이전 메시지 더 보기' 로 윈도우를 넓힌다. (수백 개 버블 동시 렌더 → 레이아웃/성능 붕괴 차단)
   */
  const HISTORY_WINDOW = 30;
  const visibleCount = ref<number>(HISTORY_WINDOW);

  const displayMessages = computed<Message[]>(() => {
    if (chatMessages.value.length === 0) {
      return [buildWelcomePromptMessage() as unknown as Message];
    }
    const all = chatMessages.value;
    return all.length > visibleCount.value ? all.slice(all.length - visibleCount.value) : all;
  });

  /** 윈도우 밖(위쪽)에 더 오래된 메시지가 남아있는지. */
  const hasMoreHistory = computed<boolean>(() => chatMessages.value.length > visibleCount.value);
  /** 윈도우 밖 메시지 수 (버튼 라벨용). */
  const hiddenHistoryCount = computed<number>(() =>
    Math.max(0, chatMessages.value.length - visibleCount.value),
  );
  /** '이전 메시지 더 보기' — 윈도우를 HISTORY_WINDOW 만큼 확장. */
  const showEarlierMessages = (): void => {
    visibleCount.value += HISTORY_WINDOW;
  };

  // 채팅창을 (재)열 때마다 윈도우를 최근 N 으로 리셋 — 긴 히스토리로 열어도 최근만 렌더.
  watch(isChatWindowOpen, (open) => {
    if (open) visibleCount.value = HISTORY_WINDOW;
  });

  const onEnter = async (e: KeyboardEvent) => {
    e.preventDefault();
    await sendMessage();
  };

  /**
   * 사용자가 직접 스크롤을 위로 올렸는지 추적.
   * 핵심: 토큰 streaming 으로 콘텐츠가 늘어나면 자동으로 scrollTop 을 끌어내리는데, 그때
   * 발생하는 scroll 이벤트와 사용자가 wheel / 키보드로 일으킨 scroll 이벤트를 분간해야 한다.
   * 사용자 의도 이벤트(wheel / touchstart / keydown PageUp 등)가 발생했을 때만 userScrolledUp 토글.
   * 사용자가 다시 바닥 근처로 내려오면 자동 follow 재개.
   */
  const STICK_THRESHOLD_PX = 80;
  const userScrolledUp = ref(false);

  /** 스크롤이 바닥 근처가 아닐 때 노출되는 '맨 아래로' 버튼 표시 여부. */
  const showScrollToBottom = ref(false);

  const isNearBottom = (el: HTMLElement) =>
    el.scrollHeight - (el.scrollTop + el.clientHeight) <= STICK_THRESHOLD_PX;

  const updateScrollToBottomBtn = () => {
    showScrollToBottom.value = !!container.value && !isNearBottom(container.value);
  };

  /** scroll 이벤트 자체로는 userScrolledUp 을 갱신하지 않음 (자동 스크롤도 scroll 이벤트를 발생시키므로). */
  const handleScroll = () => {
    if (!container.value) return;
    scrollPosition.value = {
      scrollTop: container.value.scrollTop,
    };
    // 사용자가 바닥으로 다시 내려오면 follow 재개 (userScrolledUp 자동 해제만 허용).
    if (userScrolledUp.value && isNearBottom(container.value)) {
      userScrolledUp.value = false;
    }
    updateScrollToBottomBtn();
  };

  /** '맨 아래로' 버튼 클릭 — 바닥으로 부드럽게 스크롤 + follow 재개. */
  const scrollToBottom = () => {
    if (!container.value) return;
    userScrolledUp.value = false;
    container.value.scrollTo({ top: container.value.scrollHeight, behavior: 'smooth' });
    showScrollToBottom.value = false;
  };

  /** 사용자 의도 이벤트 — 이때만 위로 스크롤 잠금 활성화. */
  const onUserScrollIntent = (e: Event) => {
    if (!container.value) return;
    // 위로 향하는 wheel 만 잠금 (아래로 스크롤은 follow 유지).
    if (e.type === 'wheel') {
      const we = e as WheelEvent;
      if (we.deltaY < 0) {
        userScrolledUp.value = true;
      }
      return;
    }
    if (e.type === 'touchstart') {
      // 터치 시작은 사용자 의도 — 즉시 잠금
      userScrolledUp.value = true;
      return;
    }
    if (e.type === 'keydown') {
      const ke = e as KeyboardEvent;
      if (['PageUp', 'ArrowUp', 'Home'].includes(ke.key)) {
        userScrolledUp.value = true;
      }
    }
  };

  /** 메시지 길이 / 마지막 partial 메시지 텍스트 / toolCalls 가 변할 때 모두 watch 해서 스크롤. */
  const streamingTick = computed(() => {
    const last = chatMessages.value[chatMessages.value.length - 1];
    if (!last) return '0|0|0';
    return `${chatMessages.value.length}|${(last.message || '').length}|${(last.toolCalls || []).length}`;
  });

  watch([streamingTick, isChatWindowOpen], async ([newTick, isOpen], [oldTick, prevIsOpen]) => {
    await nextTick();
    if (!container.value) return;

    // 윈도우가 새로 열린 경우: 저장된 스크롤 위치 복원 + 잠금 해제
    if (!prevIsOpen && isOpen) {
      container.value.scrollTop = scrollPosition.value.scrollTop || 0;
      userScrolledUp.value = false;
      updateScrollToBottomBtn();
      return;
    }

    // 사용자가 위로 잠금한 상태면 강제로 끌어내리지 않음 (콘텐츠가 늘면 버튼만 갱신)
    if (userScrolledUp.value) {
      updateScrollToBottomBtn();
      return;
    }

    // 메시지 개수가 늘었거나 (=새 메시지) 마지막 메시지 본문이 길어진 경우 (=token streaming) 모두 바닥으로
    if (newTick !== oldTick) {
      container.value.scrollTop = container.value.scrollHeight;
    }
    updateScrollToBottomBtn();
  });

  watch(isLoading, (loading) => {
    if (!loading) {
      nextTick(() => {
        inputRef.value?.focus();
      });
    }
  });

  /**
   * 현재 ChatWindow 마운트가 이 세션에서 처음인지 판정. true 이면 backend 의 history 를 fetch
   * (브라우저 reload 시 복원). 이미 store 에 메시지가 있으면 화면 이동 등으로 인한 remount 이므로
   * fetch 를 건너뛰어 흐름이 끊기지 않게 함.
   *
   * userInfo 가 아직 로드되지 않은 상태(reload 직후) 에서는 fetch 가 -1 로 나가 403 이 되므로
   * userInfo.userId 가 valid 해질 때까지 watch 로 재시도.
   */
  const fetchIfNeeded = async () => {
    if (chatMessages.value.length > 0) return;
    const uid = userInfo.value?.userId;
    if (uid === undefined || uid === null || uid === -1) return;
    await getChatMessage();
  };

  onMounted(async () => {
    await fetchIfNeeded();
    // 이력 조회 중 사용자가 발화해 스트림이 시작됐을 수 있다 — 공용 isLoading 의 소유자는
    // 스트림이므로, 무조건 끄면 스트림 진행 중 입력이 재활성화되어 두 번째 발화가 첫 스트림을
    // 중단시킬 수 있다 (MR !13408 리뷰 P1-4). 활성 스트림이 없을 때만 정리.
    if (!partialBotMessage.value) {
      stopLoading();
    }

    const alreadyMounted = document.querySelector('.slide-out');
    shouldRenderSlideDetail.value = !alreadyMounted;
  });

  // userInfo.userId 가 reload 후 늦게 채워질 때 재시도. immediate: true 로 첫 진입 시에도 검사.
  // (이미 valid 라면 fetchIfNeeded 가 즉시 fetch, 아직 undefined 라면 valid 가 될 때까지 대기.)
  watch(
    () => userInfo.value?.userId,
    (uid) => {
      if (uid !== undefined && uid !== null && uid !== -1) {
        fetchIfNeeded();
      }
    },
    { immediate: true },
  );

  // store 의 chatMessages 는 명시적 "초기화" 버튼이 아니면 보존한다.
  // (화면 이동으로 인한 ChatWindow 의 remount 에서도 대화가 자연스럽게 이어지도록)
  onUnmounted(() => {
    cancelStreaming();
  });

  /**
   * BotMessage 의 추천 질문 pill 클릭 → 입력창에 텍스트 채워 잠깐 보여준 뒤 즉시 전송.
   * 사용자 액션은 send 버튼 클릭과 동일한 흐름을 따른다 (사용자가 추가 액션 없이 한 번에
   * 후속 질문을 보내는 UX). 직전 메시지가 streaming 중이면 (isLoading) 새 전송을 막는다 —
   * 챗봇이 아직 응답 생성 중인데 새 user message 가 인터럽트하지 않도록.
   */
  const onSuggestionClick = async (text: string) => {
    const trimmed = text?.trim() ?? '';
    if (!trimmed) return;
    if (isLoading.value) {
      // 직전 응답이 아직 streaming 중 — 입력창에만 채워두고 사용자가 끝난 뒤 직접 보내게.
      userInput.value = trimmed;
      nextTick(() => inputRef.value?.focus());
      return;
    }
    // 입력창에 텍스트가 표시된 채 send 한다 (사용자에게 어떤 발화가 보내졌는지 한 프레임
    // 보여주는 가시성 + 디버깅용. addChatMessage 가 실제 전송 후 input 을 비움).
    userInput.value = trimmed;
    await nextTick();
    await addChatMessage(trimmed);
  };

  /**
   * 입력창의 "중지" 버튼 — 진행 중인 응답 생성을 사용자가 끊는다.
   * fetch 를 abort 하면 백엔드도 SSE 연결 종료를 감지해 남은 LLM 라운드를 중단한다.
   */
  const onStopStreaming = () => {
    cancelStreaming();
    nextTick(() => inputRef.value?.focus());
  };

  return {
    t,
    size,
    position,
    userInput,
    container,
    inputRef,
    isLoading,
    chatMessages,
    displayMessages,
    hasMoreHistory,
    hiddenHistoryCount,
    showEarlierMessages,
    isChatWindowOpen,
    isShowDetail,
    handleMessageClick,
    handleScroll,
    onUserScrollIntent,
    showScrollToBottom,
    scrollToBottom,
    toggleChat,
    sendMessage,
    startResize,
    stopDrag,
    startDrag,
    onEnter,
    shouldRenderSlideDetail,
    resetConversation,
    onSuggestionClick,
    onStopStreaming,
  };
};
